import React, { createContext, useContext, useState, useEffect } from "react";
import APIContext from "../api/api";
import { URLS } from "../../configuration/urls";
import { EcUserDataContext } from "@eclear-ag/ec-common-ui";
import { canAccessClearCustoms } from "../../pages/ClearCustoms/tools/tool";

const ParcelsContext = createContext();

const TODAY = new Date()?.toISOString()?.split("T")[0];

export const OverallKPIProvider = ({ children }) => {
  const [parcelsHandled, setParcelsHandled] = useState([]);
  const [parcelsCleared, setParcelsCleared] = useState([]);
  const [rateData, setRateData] = useState([]);
  const [dScan, setDScan] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchDate, setLastFetchDate] = useState(null);
  const apiContext = useContext(APIContext);
  const getData = apiContext?.getData;

  const { userData } = useContext(EcUserDataContext);

  // Check if user has CLEARCUSTOMS subscription
  const isSubscribedToClearCustoms = canAccessClearCustoms({ userData });

  const fetchParcelsData = async (
    currentDate = TODAY,
    forceRefresh = false
  ) => {
    if (!getData) {
      console.warn("API context not available yet");
      return;
    }
    if (
      !forceRefresh &&
      lastFetchDate === currentDate &&
      parcelsHandled.length > 0
    ) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const [
        parcelsHandledResponse,
        parcelsClearedResponse,
        dScanResponse,
        rateResponse
      ] = await Promise.all([
        getData(
          `h7/kpi/parcels-handled?currentDate=${currentDate}`,
          {},
          URLS.EC_CLEAR_CUSTOMS_BE()
        ),
        getData(
          `h7/kpi/parcels-cleared?currentDate=${currentDate}`,
          {},
          URLS.EC_CLEAR_CUSTOMS_BE()
        ),
        getData(
          `h7/kpi/d-scan?currentDate=${currentDate}`,
          {},
          URLS.EC_CLEAR_CUSTOMS_BE()
        ),
        getData(
          `h7/kpi/parcels-cleared-rate?currentDate=${currentDate}`,
          {},
          URLS.EC_CLEAR_CUSTOMS_BE()
        )
      ]);
      setParcelsHandled(parcelsHandledResponse);
      setParcelsCleared(parcelsClearedResponse);
      setDScan(dScanResponse);
      setRateData(rateResponse);
      setLastFetchDate(currentDate);
    } catch (error) {
      setError(error);
      console.log("Parcels fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (getData && isSubscribedToClearCustoms) {
      fetchParcelsData();
    }
  }, [getData, isSubscribedToClearCustoms]);

  const value = {
    parcelsHandled,
    parcelsCleared,
    dScan,
    rateData,
    isLoading,
    error,
    refetch: () => fetchParcelsData(TODAY, true),
    lastFetchDate
  };

  return (
    <ParcelsContext.Provider value={value}>{children}</ParcelsContext.Provider>
  );
};

// Custom hook to use parcels context
export const useParcels = () => {
  const context = useContext(ParcelsContext);
  if (!context) {
    throw new Error("useParcels must be used within a OverallKPIProvider");
  }
  return context;
};
